import { closeFunction } from 'components/core/dialog/C1Dialog'
import { DisplayModeContext, ModalContext } from 'lib/context/app'
import { useRouter } from 'next/router'
import { createElement, FC, ReactNode, useCallback, useMemo, useState, useEffect } from 'react'

export type ClosableProps = Record<'close', closeFunction>
export type OpenPropType = <P extends ClosableProps>(M: FC<P>, props: Omit<P, 'close'>) => () => Promise<boolean>
export type ModalContextShape = {
    open: OpenPropType
}

let i = 0

export const ModalContextProvider: FC<{ children?: ReactNode }> = ({ children }) => {
    const [modals, setModals] = useState<Record<string, ReactNode>>({})
    const router = useRouter()

    const closeModal = (k: string): void => {
        setModals((prevState) => {
            delete prevState[k]
            return { ...prevState }
        })
    }

    useEffect(() => {
        const routeChangeHander = (): void => setModals({})

        router.events.on('routeChangeStart', routeChangeHander)
        return () => { router.events.off('routeChangeStart', routeChangeHander) }
    }, [router.events])

    const open = useCallback(
        <P extends ClosableProps>(M: FC<P>, props: Omit<P, 'close'>): () => Promise<boolean> =>
            () => new Promise((resolve) => {
                const k = `${i++}`
                ;(props as P).close = (_, __, success): void => {
                    success && resolve(true)
                    closeModal(k)
                }
                setModals((prevState) => ({
                    ...prevState,
                    [k]: createElement(M, { ...(props as P), key: k }),
                }))
            }),
        [],

    )

    const openMemo = useMemo((): ModalContextShape => ({ open }), [open])

    return (
        <ModalContext.Provider value={openMemo}>
            {children}
            <DisplayModeContext.Provider value="dialog">
                {Object.keys(modals)
                    .sort()
                    .map((k) => modals[k])}
            </DisplayModeContext.Provider>
        </ModalContext.Provider>
    )
}
